import React from 'react';
import Input from '../../../ui/Input';
import { ResumeData } from '../../../../types/resume';
import { Mail, Phone, MapPin, Briefcase, Globe, Linkedin } from 'lucide-react';

interface Props {
  data: ResumeData;
  onUpdate: (data: Partial<ResumeData>) => void;
}

export default function ContactForm({ data, onUpdate }: Props) {
  const handleChange = (field: string, value: string) => {
    onUpdate({
      personalInfo: {
        ...data.personalInfo,
        [field]: value
      }
    });
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900">Contact Information</h2>
        <p className="mt-1 text-gray-600">
          Let's start with the basics. To ensure employers can reach you, input at least your name,
          email, and phone number.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Input
          label="First Name"
          value={data.personalInfo?.fullName?.split(' ')[0] || ''}
          onChange={(e) => handleChange('fullName', e.target.value)}
          placeholder="e.g. John"
          required
        />
        <Input
          label="Last Name"
          value={data.personalInfo?.fullName?.split(' ').slice(1).join(' ') || ''}
          onChange={(e) => {
            const firstName = data.personalInfo?.fullName?.split(' ')[0] || '';
            handleChange('fullName', `${firstName} ${e.target.value}`);
          }}
          placeholder="e.g. Doe"
          required
        />
      </div>

      <div className="relative">
        <Briefcase className="absolute left-3 top-9 h-5 w-5 text-gray-400" />
        <Input
          label="Professional Title"
          value={data.personalInfo?.title || ''}
          onChange={(e) => handleChange('title', e.target.value)}
          placeholder="e.g. Senior Software Engineer"
          className="pl-10"
          required
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="relative">
          <Mail className="absolute left-3 top-9 h-5 w-5 text-gray-400" />
          <Input
            label="Email"
            type="email"
            value={data.personalInfo?.email || ''}
            onChange={(e) => handleChange('email', e.target.value)}
            placeholder="e.g. john@example.com"
            className="pl-10"
            required
          />
        </div>
        <div className="relative">
          <Phone className="absolute left-3 top-9 h-5 w-5 text-gray-400" />
          <Input
            label="Phone"
            type="tel"
            value={data.personalInfo?.phone || ''}
            onChange={(e) => handleChange('phone', e.target.value)}
            placeholder="e.g. +1 (555) 123-4567"
            className="pl-10"
            required
          />
        </div>
      </div>

      <div className="relative">
        <MapPin className="absolute left-3 top-9 h-5 w-5 text-gray-400" />
        <Input
          label="Location"
          value={data.personalInfo?.location || ''}
          onChange={(e) => handleChange('location', e.target.value)}
          placeholder="e.g. New York, NY"
          className="pl-10"
          required
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="relative">
          <Linkedin className="absolute left-3 top-9 h-5 w-5 text-gray-400" />
          <Input
            label="LinkedIn Profile"
            value={data.personalInfo?.linkedin || ''}
            onChange={(e) => handleChange('linkedin', e.target.value)}
            placeholder="e.g. linkedin.com/in/johndoe"
            className="pl-10"
          />
        </div>
        <div className="relative">
          <Globe className="absolute left-3 top-9 h-5 w-5 text-gray-400" />
          <Input
            label="Personal Website"
            value={data.personalInfo?.website || ''}
            onChange={(e) => handleChange('website', e.target.value)}
            placeholder="e.g. johndoe.com"
            className="pl-10"
          />
        </div>
      </div>
    </div>
  );
}